import { useMemo } from 'react';
// API
import { useEnv } from '../../../../../context/env-context';
import {
  BasisTheoryProvider,
  CardExpirationDateElement,
  CardNumberElement,
  CardVerificationCodeElement,
} from '@basis-theory/react-elements';
import {
  VISA,
  MASTERCARD,
  AMERICAN_EXPRESS,
  DISCOVER,
} from '@basis-theory/basis-theory-js/types/elements';
// components
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';

function ConsumerCardInputForm({
  bt,
  btElementsEvents,
  btMetadata,
  setBtElementsEvents,
  setBtMetadata,
  cardNumberRef,
  cardExpirationDateRef,
  cardVerificationCodeRef,
}) {
  const { envName } = useEnv();

  const customCardTypes = useMemo(() => {
    let cardTypes = [VISA, MASTERCARD, AMERICAN_EXPRESS, DISCOVER];

    if (envName === 'dev' || envName === 'stage') {
      cardTypes = [
        ...cardTypes,
        {
          code: { size: 3, name: 'CVV' },
          gaps: [4, 8, 12],
          lengths: [16],
          niceType: 'KOREAN',
          patterns: [9],
          type: 'korean',
        },
      ];
    }

    return cardTypes;
  }, [envName]);

  const getBtElementContainerStyle = fieldName => {
    let className = 'bo-consumer-bt-element-container';

    if (fieldName === 'cvc' || fieldName === 'cardExpirationDate') {
      className += ' bo-w-100';
    }

    if (btElementsEvents[fieldName] && btElementsEvents[fieldName].focused) {
      className += ' bo-consumer-bt-element-container-focused';
    }

    if (
      fieldName === 'cvc' &&
      (envName === 'dev' || envName === 'stage') &&
      (btElementsEvents.cvc.changeEvent === null || btElementsEvents.cvc.changeEvent?.empty) &&
      btElementsEvents.cardNumber.changeEvent?.cardBrand === 'korean'
    ) {
      return className;
    }

    if (
      btElementsEvents[fieldName] &&
      btElementsEvents[fieldName].dirty &&
      btElementsEvents[fieldName].changeEvent &&
      (btElementsEvents[fieldName].changeEvent.empty ||
        !btElementsEvents[fieldName].changeEvent.valid)
    ) {
      className += ' bo-consumer-bt-element-container-invalid';
    }

    return className;
  };

  const btElementStyle = {
    base: {
      padding: 5,
      color: '#000000',
      fontSize: '13px',
      fontWeight: 500,
      '::placeholder': {
        color: '#979797',
      },
    },
    invalid: {
      color: '#000000',
    },
    complete: {
      color: '#000000',
    },
  };

  const getDefaultInputStyle = fieldName => {
    let className = 'bo-common-popover-form-menu-input';

    if (btMetadata[fieldName].dirty && !btMetadata[fieldName].value) {
      className += ' bo-consumer-card-form-default-input-invalid';
    }

    return className;
  };

  const handleBtElementReadyEvent = fieldName =>
    setBtElementsEvents(prevEvents => ({
      ...prevEvents,
      [fieldName]: { ...prevEvents[fieldName], ready: true },
    }));

  const handleBtElementFocusEvent = fieldName =>
    setBtElementsEvents(prevEvents => ({
      ...prevEvents,
      [fieldName]: {
        ...prevEvents[fieldName],
        focused: true,
        dirty: true,
        changeEvent: prevEvents[fieldName].changeEvent,
      },
    }));

  const handleBtElementBlurEvent = fieldName =>
    setBtElementsEvents(prevEvents => ({
      ...prevEvents,
      [fieldName]: { ...prevEvents[fieldName], focused: false },
    }));

  const handleBtElementChangeEvent = (fieldName, e) =>
    setBtElementsEvents(prevEvents => ({
      ...prevEvents,
      [fieldName]: {
        ...prevEvents[fieldName],
        changeEvent: prevEvents[fieldName].dirty ? e : null,
      },
    }));

  const handleDefaultInputChangeEvent = (fieldName, e) =>
    setBtMetadata(prevM => ({
      ...prevM,
      [fieldName]: { value: e.target.value, dirty: true },
    }));

  return (
    <Row className="mt-2">
      <Col>
        <BasisTheoryProvider bt={bt}>
          <div>
            <Form.Label className="bo-common-popover-form-menu-label">Debit Card Number</Form.Label>
            <div className={getBtElementContainerStyle('cardNumber')}>
              <CardNumberElement
                id="cardNumber"
                ref={cardNumberRef}
                iconPosition="none"
                placeholder=""
                style={btElementStyle}
                onReady={() => handleBtElementReadyEvent('cardNumber')}
                onFocus={() => handleBtElementFocusEvent('cardNumber')}
                onChange={e => handleBtElementChangeEvent('cardNumber', e)}
                onBlur={() => handleBtElementBlurEvent('cardNumber')}
                cardTypes={customCardTypes}
              />
            </div>
          </div>
          <div className="d-flex justify-content-between">
            <div>
              <Form.Label className="bo-common-popover-form-menu-label">MM/YY</Form.Label>
              <div className={getBtElementContainerStyle('cardExpirationDate')}>
                <CardExpirationDateElement
                  id="cardExpirationDate"
                  ref={cardExpirationDateRef}
                  placeholder=""
                  style={btElementStyle}
                  onReady={() => handleBtElementReadyEvent('cardExpirationDate')}
                  onFocus={() => handleBtElementFocusEvent('cardExpirationDate')}
                  onChange={e => handleBtElementChangeEvent('cardExpirationDate', e)}
                  onBlur={() => handleBtElementBlurEvent('cardExpirationDate')}
                />
              </div>
            </div>
            <div>
              <Form.Label className="bo-common-popover-form-menu-label">CVV</Form.Label>
              <div className={getBtElementContainerStyle('cvc')}>
                <CardVerificationCodeElement
                  id="cardVerificationCode"
                  ref={cardVerificationCodeRef}
                  placeholder=""
                  style={btElementStyle}
                  onReady={() => handleBtElementReadyEvent('cvc')}
                  onFocus={() => handleBtElementFocusEvent('cvc')}
                  onChange={e => handleBtElementChangeEvent('cvc', e)}
                  onBlur={() => handleBtElementBlurEvent('cvc')}
                />
              </div>
            </div>
          </div>
        </BasisTheoryProvider>
        <div className="d-flex justify-content-between gap-2">
          <div className="w-50">
            <Form.Label className="bo-common-popover-form-menu-label">First Name</Form.Label>
            <Form.Control
              id="firstName"
              className={getDefaultInputStyle('firstName')}
              placeholder=""
              value={btMetadata.firstName.value}
              onChange={e => handleDefaultInputChangeEvent('firstName', e)}
            />
          </div>
          <div className="w-50">
            <Form.Label className="bo-common-popover-form-menu-label">Last Name</Form.Label>
            <Form.Control
              id="lastName"
              className={getDefaultInputStyle('lastName')}
              placeholder=""
              value={btMetadata.lastName.value}
              onChange={e => handleDefaultInputChangeEvent('lastName', e)}
            />
          </div>
        </div>
      </Col>
    </Row>
  );
}

export default ConsumerCardInputForm;
